import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  CircularProgress, 
  TablePagination, 
  MenuItem,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  Search as SearchIcon,
  Clear as ClearIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../../api';

const AttendanceOverdueReportView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [streak, setStreak] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('ALL');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchOverdueReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/attendance-overdue/report?streak=${streak}`);
      setData(res.data || []);
      setPage(0);
    } catch (err) {
      console.error("Failed to fetch overdue report", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (streak !== '' && !isNaN(streak) && streak > 0) {
        fetchOverdueReport();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [streak]);

  const departments = ['ALL', ...new Set(data.map(item => item.department))];

  const filteredData = data.filter(item => {
    const matchesSearch = 
      item.emp_id?.toLowerCase().includes(search.toLowerCase()) || 
      item.employee_name?.toLowerCase().includes(search.toLowerCase());
    const matchesDept = selectedDept === 'ALL' || item.department === selectedDept;
    return matchesSearch && matchesDept;
  });

  const exportToExcel = () => {
    if (filteredData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(filteredData.map(row => ({
      "Emp ID": row.emp_id,
      "Name": row.employee_name,
      "Department": row.department,
      "Designation": row.designation,
      "Status": row.employee_status,
      "Last Attendance": row.last_attendance_date || "Never (New Hire)",
      "Absent Days": row.absent_days
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Overdue_Report");
    XLSX.writeFile(workbook, `Attendance_Overdue_Streak_${streak}Days.xlsx`);
  };

  const handleClearSearch = () => setSearch('');

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, bgcolor: '#ffffff', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <PeopleIcon sx={{ color: '#c62828', mr: 1.5, fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10394f' }}>
          Attendance Overdue Report
        </Typography>
      </Box>

      {/* FILTER SECTION */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5, 
          mb: 3, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap'
        }}>
          <TextField
            size="small"
            placeholder="Search Employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <TextField
            select
            size="small"
            label="Department"
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {departments.map(dept => (
              <MenuItem key={dept} value={dept}>
                {dept === 'ALL' ? 'All Departments' : dept}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="number"
            label="Absent Days"
            value={streak}
            onChange={(e) => setStreak(Number(e.target.value))}
            sx={{ width: 140 }}
            InputProps={{
              inputProps: { min: 1 }
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Export to Excel">
            <IconButton 
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
              sx={{ 
                color: '#2e7d32',
                '&:hover': {
                  bgcolor: 'rgba(46, 125, 50, 0.08)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* RESULTS SUMMARY */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 1.5,
        px: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PeopleIcon fontSize="small" color="action" />
          <Typography variant="body2" color="textSecondary">
            {filteredData.length} employee{filteredData.length !== 1 ? 's' : ''} found
          </Typography>
          <Chip 
            label={`Streak: ${streak}+ days`} 
            size="small" 
            color="error"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      </Box>

      {/* DATA TABLE */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Emp ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Employee Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>Last Attendance</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f8f9fa',
                      color: 'error.main',
                      textAlign: 'center'
                    }}>
                      Absent Days
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow 
                        key={row.emp_id || index}
                        hover
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                          '&:hover': { bgcolor: '#f0f7ff' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.emp_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.employee_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.department}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {row.designation}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={row.employee_status} 
                            size="small"
                            color={row.employee_status === 'Active' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {row.last_attendance_date ? (
                            <Typography variant="body2">
                              {new Date(row.last_attendance_date).toLocaleDateString()}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Never <span style={{ fontSize: '0.75rem', color: '#888' }}>
                                (DOJ: {row.date_of_joining || 'N/A'})
                              </span>
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip 
                            label={row.absent_days} 
                            size="small"
                            color="error"
                            sx={{ fontWeight: 700, minWidth: 40 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="textSecondary">
                          No employees found matching the criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ 
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fafafa'
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceOverdueReportView;